import React, { useState, useEffect } from "react";
import Modal from "../../components/Modal";
import { Agent, DayOfWeek, RosterDay } from "../../util/types";

interface SwapFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwap: (
    day: DayOfWeek,
    agent1Id: number,
    agent1ShiftName: string,
    agent2Id: number,
    agent2ShiftName: string,
  ) => Promise<void>;
  roster: RosterDay[];
}

const SwapFormModal: React.FC<SwapFormModalProps> = ({
  isOpen,
  onClose,
  onSwap,
  roster,
}) => {
  const [selectedDay, setSelectedDay] = useState<DayOfWeek | "">("");
  const [selectedAgent1Id, setSelectedAgent1Id] = useState<number | "">("");
  const [selectedAgent2Id, setSelectedAgent2Id] = useState<number | "">("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedDay("");
      setSelectedAgent1Id("");
      setSelectedAgent2Id("");
      setError(null);
    }
  }, [isOpen]);

  // Get assigned agents for the selected day
  const getAssignedAgentsForDay = () => {
    if (!selectedDay) return [];

    const dayRoster = roster.find((d) => d.day === selectedDay);
    if (!dayRoster) return [];

    const assignedAgents: Array<{ agent: Agent; shiftName: string }> = [];
    dayRoster.shifts.forEach((shift) => {
      shift.assignedAgents.forEach((agent) => {
        assignedAgents.push({ agent, shiftName: shift.shiftName });
      });
    });

    return assignedAgents;
  };

  const assignedAgents = getAssignedAgentsForDay();

  // Find shift name for selected agents
  const getShiftForAgent = (agentId: number) => {
    const agentInfo = assignedAgents.find((a) => a.agent.id === agentId);
    return agentInfo?.shiftName || "";
  };

  const agent1ShiftName = selectedAgent1Id
    ? getShiftForAgent(selectedAgent1Id as number)
    : "";
  const agent2ShiftName = selectedAgent2Id
    ? getShiftForAgent(selectedAgent2Id as number)
    : "";

  // Get shift details including gender preferences
  const getShiftDetails = (shiftName: string) => {
    if (!selectedDay) return null;
    const dayRoster = roster.find((d) => d.day === selectedDay);
    return dayRoster?.shifts.find((s) => s.shiftName === shiftName) || null;
  };

  // Get agent details
  const getAgentDetails = (agentId: number) => {
    return assignedAgents.find((a) => a.agent.id === agentId)?.agent || null;
  };

  // Validate gender compatibility for swap
  const validateGenderCompatibility = (): string | null => {
    if (!selectedAgent1Id || !selectedAgent2Id) return null;

    const agent1 = getAgentDetails(selectedAgent1Id as number);
    const agent2 = getAgentDetails(selectedAgent2Id as number);
    const shift1 = getShiftDetails(agent1ShiftName);
    const shift2 = getShiftDetails(agent2ShiftName);

    if (!agent1 || !agent2 || !shift1 || !shift2) return null;

    // Check if Agent 1 can go to Agent 2's shift (shift2)
    if (shift2.onlyMale && agent1.gender !== "Male") {
      return `❌ Cannot swap: ${agent1.agentName} (${agent1.gender}) cannot be assigned to "${shift2.shiftName}" which only allows Male agents.`;
    }
    if (shift2.onlyFemale && agent1.gender !== "Female") {
      return `❌ Cannot swap: ${agent1.agentName} (${agent1.gender}) cannot be assigned to "${shift2.shiftName}" which only allows Female agents.`;
    }

    // Check if Agent 2 can go to Agent 1's shift (shift1)
    if (shift1.onlyMale && agent2.gender !== "Male") {
      return `❌ Cannot swap: ${agent2.agentName} (${agent2.gender}) cannot be assigned to "${shift1.shiftName}" which only allows Male agents.`;
    }
    if (shift1.onlyFemale && agent2.gender !== "Female") {
      return `❌ Cannot swap: ${agent2.agentName} (${agent2.gender}) cannot be assigned to "${shift1.shiftName}" which only allows Female agents.`;
    }

    return null; // Swap is valid
  };

  // Filter agents for Agent 2 dropdown - exclude agents from the same shift as Agent 1
  const getAvailableAgentsForAgent2 = () => {
    if (!selectedAgent1Id) return assignedAgents;
    return assignedAgents.filter(
      ({ shiftName }) => shiftName !== agent1ShiftName,
    );
  };

  const availableAgentsForAgent2 = getAvailableAgentsForAgent2();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!selectedDay) {
      setError("Please select a day");
      return;
    }
    if (!selectedAgent1Id || !selectedAgent2Id) {
      setError("Please select both agents");
      return;
    }
    if (selectedAgent1Id === selectedAgent2Id) {
      setError(
        "Cannot swap an agent with themselves. Please select different agents.",
      );
      return;
    }
    if (agent1ShiftName === agent2ShiftName) {
      setError(
        "Both agents are in the same shift. Please select agents from different shifts.",
      );
      return;
    }

    // Gender compatibility validation
    const genderError = validateGenderCompatibility();
    if (genderError) {
      setError(genderError);
      return;
    }

    setIsSubmitting(true);
    try {
      await onSwap(
        selectedDay as DayOfWeek,
        selectedAgent1Id as number,
        agent1ShiftName,
        selectedAgent2Id as number,
        agent2ShiftName,
      );
      onClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to swap shifts";
      // Check if it's a 400 error related to gender
      if (
        errorMessage.includes("400") ||
        errorMessage.includes("gender") ||
        errorMessage.includes("Gender")
      ) {
        setError(
          "❌ Swap rejected by server: Gender restrictions prevent this swap. Please check shift gender preferences.",
        );
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} header="Swap Agents">
      <form onSubmit={handleSubmit}>
        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Day Selection */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Select Day<span className="text-red-500"> *</span>
          </label>
          <select
            value={selectedDay}
            onChange={(e) => {
              setSelectedDay(e.target.value as DayOfWeek);
              setSelectedAgent1Id("");
              setSelectedAgent2Id("");
            }}
            className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors ${!selectedDay
                ? "text-gray-400 dark:text-gray-400"
                : "text-gray-900 dark:text-gray-100"
              }`}
            required
          >
            <option value="" disabled hidden>
              Select a day
            </option>
            {roster.map((day) => (
              <option
                key={day.day}
                value={day.day}
                className="text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700"
              >
                {day.day}
              </option>
            ))}
          </select>
        </div>

        {/* Agent 1 Selection */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Agent 1<span className="text-red-500"> *</span>
          </label>
          <select
            value={selectedAgent1Id}
            onChange={(e) => {
              setSelectedAgent1Id(e.target.value ? Number(e.target.value) : "");
              setSelectedAgent2Id(""); // Reset Agent 2 when Agent 1 changes
            }}
            className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${!selectedAgent1Id
                ? "text-gray-400 dark:text-gray-400"
                : "text-gray-900 dark:text-gray-100"
              }`}
            disabled={!selectedDay}
            required
          >
            <option value="" disabled hidden>
              Select an agent
            </option>
            {assignedAgents.map(({ agent, shiftName }) => {
              const shift = getShiftDetails(shiftName);
              const shiftGenderInfo = shift?.onlyMale
                ? " [Male Only Shift]"
                : shift?.onlyFemale
                  ? " [Female Only Shift]"
                  : " [Both Genders]";
              return (
                <option
                  key={agent.id}
                  value={agent.id}
                  className="text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700"
                >
                  {agent.agentName} ({agent.gender}) - {shiftName}
                  {shiftGenderInfo}
                </option>
              );
            })}
          </select>
        </div>

        {/* Agent 2 Selection */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Agent 2<span className="text-red-500"> *</span>
          </label>
          <select
            value={selectedAgent2Id}
            onChange={(e) =>
              setSelectedAgent2Id(e.target.value ? Number(e.target.value) : "")
            }
            className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${!selectedAgent2Id
                ? "text-gray-400 dark:text-gray-400"
                : "text-gray-900 dark:text-gray-100"
              }`}
            disabled={!selectedDay || !selectedAgent1Id}
            required
          >
            <option value="" disabled hidden>
              Select an agent
            </option>
            {availableAgentsForAgent2.map(({ agent, shiftName }) => {
              const shift = getShiftDetails(shiftName);
              const shiftGenderInfo = shift?.onlyMale
                ? " [Male Only Shift]"
                : shift?.onlyFemale
                  ? " [Female Only Shift]"
                  : " [Both Genders]";
              return (
                <option
                  key={agent.id}
                  value={agent.id}
                  className="text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700"
                >
                  {agent.agentName} ({agent.gender}) - {shiftName}
                  {shiftGenderInfo}
                </option>
              );
            })}
          </select>
          {selectedAgent1Id && availableAgentsForAgent2.length === 0 && (
            <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
              No other agents available in different shifts on this day.
            </p>
          )}
        </div>

        {/* Preview */}
        {selectedAgent1Id &&
          selectedAgent2Id &&
          selectedAgent1Id !== selectedAgent2Id && (
            <>
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Swap Preview:
                </p>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <p>
                    •{" "}
                    <span className="font-semibold">
                      {
                        assignedAgents.find(
                          (a) => a.agent.id === selectedAgent1Id,
                        )?.agent.agentName
                      }
                    </span>{" "}
                    will move from{" "}
                    <span className="font-semibold">{agent1ShiftName}</span> to{" "}
                    <span className="font-semibold">{agent2ShiftName}</span>
                  </p>
                  <p>
                    •{" "}
                    <span className="font-semibold">
                      {
                        assignedAgents.find(
                          (a) => a.agent.id === selectedAgent2Id,
                        )?.agent.agentName
                      }
                    </span>{" "}
                    will move from{" "}
                    <span className="font-semibold">{agent2ShiftName}</span> to{" "}
                    <span className="font-semibold">{agent1ShiftName}</span>
                  </p>
                </div>
              </div>

              {/* Gender Compatibility Check */}
              {(() => {
                const genderError = validateGenderCompatibility();
                if (genderError) {
                  return (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 rounded-lg text-sm">
                      <p className="font-semibold mb-1">
                        ⚠️ Gender Compatibility Issue:
                      </p>
                      <p>{genderError}</p>
                    </div>
                  );
                } else {
                  return (
                    <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 rounded-lg text-sm">
                      <p className="font-semibold">
                        ✅ Swap is valid - Gender requirements are satisfied
                      </p>
                    </div>
                  );
                }
              })()}
            </>
          )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold px-4 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              isSubmitting ||
              !selectedDay ||
              !selectedAgent1Id ||
              !selectedAgent2Id
            }
            className="bg-indigo-600 text-white font-semibold px-4 py-2 rounded-lg shadow-md hover:bg-indigo-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Swapping..." : "Confirm Swap"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default SwapFormModal;